import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { NotificationsService } from '../../misc/notifications/notifications.service';
import { Const } from '../../../../core/helpers/Const';
import { ServiceModule } from '../../../service.module';

export interface PaginatedResponse<T> {
    data: T;
    continueToken: string;
    totalCount: number;
}

@Injectable({ providedIn: ServiceModule })
export class HttpService<T> {

    constructor(protected httpClient: HttpClient, protected notificationsService: NotificationsService) {}

    getList(url: string): Observable<T[]> {
        return this.httpClient.get<T[]>(url).pipe(catchError(this.handleError.bind(this)));
    }

    getPaginatedList<T>(url: string): Observable<PaginatedResponse<T>> {
        return this.httpClient.get<T>(url, { observe: 'response' }).pipe(
            map((response: HttpResponse<T>) => {
                const continueToken = response.headers.get('Pagination-Token') || '';
                const totalCount = response.headers.get('Pagination-Total-Count') || '';

                const data = response.body as T;
                return { data, continueToken, totalCount: parseInt(totalCount)};
            }),
            catchError(this.handleError.bind(this))
        );
    }

    get(url: string): Observable<T> {
        return this.httpClient.get<T>(url).pipe(catchError(this.handleError.bind(this)));
    }

    post(url: string, resource: T): Observable<any> {
        return this.httpClient.post(url, resource).pipe(catchError(this.handleError.bind(this)));
    }

    postFile(url: string, formData: FormData, params = {}) {
        return this.httpClient.post(url, formData, params).pipe(catchError(this.handleError.bind(this)));
    }

    delete(url: string): Observable<T> {
        return this.httpClient.delete<T>(url).pipe(catchError(this.handleError.bind(this)));
    }

    update(url: string, resource: T): Observable<any> {
        return this.httpClient.put(url, resource).pipe(catchError(this.handleError.bind(this)));
    }

    queryPolicies(url: string, resource: T): Observable<T[]> {
        // queryPolicies uses POST since the required query parameters can be too large
        // for a URL, potentially exceeding length limits, making a JSON request body a better choice.
        return this.httpClient.post<T[]>(url, resource).pipe(catchError(this.handleError.bind(this)));
    }

    handleError(error: HttpErrorResponse) {
        // Handle the HTTP error here
        if (error.status === Const.NOT_LOGGED_IN_HTTP_CODE) {
            // Only show session expiry dialog for actual session invalidation,
            // not for authorization errors (which components handle individually)
            const errorMsg = typeof error.error === 'string'
                ? error.error
                : (error.error?.message || error.error?.error || '');
            if (errorMsg.toLowerCase().includes('workspace') && errorMsg.toLowerCase().includes('session')) {
                this.notificationsService.showSessionExpiryDialog();
            }
        }
        if (error.error) {
            if (error.status === 0 && error.error instanceof ProgressEvent) {
                return throwError(() => new CustomError('There was a network error.', error.status));
            } else {
                return throwError(() => new CustomError(error.error, error.status));
            }
        }
        return throwError(() => new CustomError('Something went wrong.', error.status));
    }
}

export class CustomError extends Error {
    statusCode: number;
    constructor(message: string, statusCode: number) {
        super(message);
        this.statusCode = statusCode;
    }
}

